import { useEffect, useLayoutEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useCatalog } from "../catalog/CatalogContext";
import {
  filtersFromSearchParams,
  filtersToSearchParams,
} from "../catalog/catalog-filters";
import { useCollection } from "../collection/CollectionContext";
import { Button } from "../components/Button";
import { ErrorMessage } from "../components/ErrorMessage";
import { LoadingIndicator } from "../components/LoadingIndicator";
import { ReleaseCard } from "../components/ReleaseCard";
import { useGridDensity } from "../ui/GridDensityContext";

function restoreWindowScroll(top: number) {
  try {
    window.scrollTo(0, top);
  } catch {
    document.documentElement.scrollTop = top;
  }
}

export function CatalogPage() {
  const catalog = useCatalog();
  const collection = useCollection();
  const { density } = useGridDensity();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessionReady, setSessionReady] = useState(
    () => filtersFromSearchParams(searchParams) === null,
  );

  useEffect(() => {
    const fromUrl = filtersFromSearchParams(searchParams);
    if (fromUrl) {
      catalog.applyFilters(fromUrl);
    }
    setSessionReady(true);
  }, []);

  useEffect(() => {
    if (!sessionReady) {
      return;
    }
    const desired = filtersToSearchParams(catalog.filters);
    if (desired.toString() !== searchParams.toString()) {
      setSearchParams(desired, { replace: true });
    }
  }, [catalog.filters, searchParams, sessionReady, setSearchParams]);

  useEffect(() => {
    if (!sessionReady) {
      return;
    }
    void catalog.ensureResults();
  }, [sessionReady, catalog.fingerprint, catalog.ensureResults]);

  useLayoutEffect(() => {
    restoreWindowScroll(catalog.scrollTop);
    return () => {
      catalog.setScrollTop(window.scrollY || document.documentElement.scrollTop || 0);
    };
  }, []);

  const resultsReady = catalog.loadedFingerprint === catalog.fingerprint;
  const showItems = resultsReady && catalog.items.length > 0;

  return (
    <section className="hw-page hw-page--catalog">
      <h1 className="hw-visually-hidden">Каталог</h1>

      {collection.conflictMessage ? (
        <ErrorMessage>{collection.conflictMessage}</ErrorMessage>
      ) : null}
      {collection.errorMessage ? (
        <ErrorMessage>{collection.errorMessage}</ErrorMessage>
      ) : null}

      {catalog.loading ? <LoadingIndicator label="Поиск…" /> : null}

      {!catalog.loading && catalog.error ? (
        <ErrorMessage>{catalog.error}</ErrorMessage>
      ) : null}

      {!catalog.loading && resultsReady && catalog.items.length === 0 && !catalog.error ? (
        <p className="hw-empty" role="status">
          Ничего не найдено. Попробуйте другой запрос или сбросьте фильтры.
        </p>
      ) : null}

      {showItems ? (
        <ul className={`hw-release-grid hw-release-grid--catalog hw-release-grid--cols-${density}`}>
          {catalog.items.map((release, index) => (
            <li key={release.releaseId}>
              <ReleaseCard
                release={release}
                variant="catalog"
                priorityImage={index < 4}
              />
            </li>
          ))}
        </ul>
      ) : null}

      {!catalog.loading && resultsReady && catalog.nextCursor ? (
        <div className="hw-load-more">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              void catalog.loadMore();
            }}
            disabled={catalog.loadingMore}
          >
            {catalog.loadingMore ? "Загрузка…" : "Показать ещё"}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
